import { createApp, type Component } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import './styles.css';
import { initStorage, loadSettings, logger, requestPersistence } from './platform';
import { locale } from './i18n';
import { useApp } from './stores/app';
import { installFonts } from './fonts';
import { installTheme } from './theme';
import { applyFavicon } from './branding';
import { openSavedLocation, storagePlace } from './storage-location';
import FolderGate from './components/FolderGate.vue';
import ProfileGate from './components/ProfileGate.vue';
import { startProfiles, type GateMode } from './profiles/session';
import { installProfileLock } from './profiles/lock';
import { installPwa } from './pwa';
import { maybeWelcome } from './welcome';
import { maybeShowChangelog } from './changelog';
import { loadFeedback } from './feedback';
import { watchSegPills } from './seg-pills';
import { installDismissGuard } from './editor/dismiss';

installDismissGuard();
// before the profile gate: its segmented controls need the same sliding highlight
watchSegPills();

async function boot(): Promise<void> {
  document.documentElement.lang = locale.value;
  // the profile first (docs/TZ-PROFILES.md): its database, folder, language and consent decide what is read
  const gate = await startProfiles();
  if (gate) await profileGate(gate);
  document.documentElement.lang = locale.value;
  await initStorage();
  await loadSettings();
  installTheme();
  // the canvases may be in a folder: open it (or ask for access) before anything reads them
  await openSavedLocation();
  if (storagePlace.gate) await folderGate();
  window.addEventListener('error', (e) => logger.error('ui.error', { code: e.error?.name ?? 'Error' }));
  window.addEventListener('unhandledrejection', (e) => logger.error('ui.rejection', { code: (e.reason as Error)?.name ?? 'unknown' }));
  const app = createApp(App);
  app.use(createPinia());
  app.mount('#app');
  // a bundled font finished loading: re-measure and redraw text on every open canvas
  installFonts(() => {
    for (const s of useApp().sessions.values()) s.scene.invalidateObjects(null);
  });
  void requestPersistence();
  void applyFavicon();
  installPwa();
  installProfileLock();
  void maybeWelcome();
  void loadFeedback();
  void maybeShowChangelog();
  void logger.prune();
}


/** A small screen before the app (it calls `onDone` when the app may start). */
function gateScreen(component: Component, props: Record<string, unknown> = {}): Promise<void> {
  return new Promise((resolve) => {
    const gate = createApp(component, {
      ...props,
      onDone: () => {
        gate.unmount();
        resolve();
      },
    });
    gate.mount('#app');
  });
}

/** No profile is open yet: create one, pick one, or enter its PIN. */
const profileGate = (mode: GateMode) => gateScreen(ProfileGate, { mode });
/** The folder with the canvases needs a click to be opened again. */
const folderGate = () => gateScreen(FolderGate);

void boot();
